import React, { useState } from 'react';
import { Star, TrendingUp, Award, BarChart3, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import AnimatedNumber from '../AnimatedNumber';
import AnimatedProgressBar from '../AnimatedProgressBar';
import ScrollReveal from '../ScrollReveal';

const Results = () => {
  return (
    <section id="results" className="py-16 lg:py-20 bg-white relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-stone-200 to-transparent" />
      
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-16">
          <p className="text-amber-800/80 text-base tracking-[0.2em] uppercase mb-4">
            Track Record
          </p>
          <h2 className="text-4xl md:text-5xl font-light text-stone-800 mb-6">
            数字で見る実績
          </h2>
          <div className="w-20 h-px bg-amber-700/40 mx-auto" />
        </div>

        {/* Two column layout */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          
          {/* Left: Airbnb Performance */}
          <ScrollReveal direction="left">
          <div className="bg-gradient-to-br from-stone-50 to-amber-50/30 rounded-2xl p-8 lg:p-10">
            <div className="flex items-center justify-between gap-3 mb-8">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-white shadow-sm flex items-center justify-center">
                  <Star className="w-6 h-6 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-xl font-medium text-stone-800">ゲスト評価実績</h3>
                  <p className="text-base text-stone-500">直近1年間のAirbnb実績</p>
                </div>
              </div>
              <a
                href="https://www.airbnb.com/users/show/318242986"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-sm text-amber-700 hover:text-amber-800 transition-colors bg-white px-3 py-2 rounded-lg shadow-sm border border-stone-200 hover:border-amber-300"
              >
                <span>Airbnbで見る</span>
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>

            {/* Rating highlight */}
            <ScrollReveal delay={100}>
              <div className="bg-white rounded-xl p-6 mb-6 shadow-sm border border-stone-100">
                <div className="flex items-end justify-between mb-4">
                  <div>
                    <p className="text-base text-stone-500 mb-1">総合評価</p>
                    <div className="flex items-baseline gap-2">
                      <AnimatedNumber 
                        value={4.96} 
                        decimals={2}
                        className="text-6xl font-light text-stone-800"
                      />
                      <span className="text-stone-400 text-lg">/ 5</span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        className="w-6 h-6 fill-amber-400 text-amber-400" 
                      />
                    ))}
                  </div>
                </div>
              
              {/* Quality comparison */}
              <div className="pt-4 border-t border-stone-100">
                <p className="text-base text-stone-500 mb-3">総合品質スコア</p>
                <div className="space-y-3">
                  {/* ACOE score */}
                  <div>
                    <div className="flex justify-between text-base mb-1">
                      <span className="text-stone-700 font-medium">ACOE</span>
                      <span className="text-amber-700 font-semibold">96.9%</span>
                    </div>
                    <AnimatedProgressBar 
                      value={96.9}
                      duration={1500}
                      barClassName="bg-gradient-to-r from-amber-500 to-amber-600"
                    />
                  </div>
                  {/* Market average */}
                  <div>
                    <div className="flex justify-between text-base mb-1">
                      <span className="text-stone-500">市場平均</span>
                      <span className="text-stone-500">85.1%</span>
                    </div>
                    <AnimatedProgressBar 
                      value={85.1}
                      duration={1500}
                      barClassName="bg-stone-300"
                    />
                  </div>
                </div>
              </div>
            </div>
            </ScrollReveal>

            <p className="text-stone-500 text-base font-light text-center">
              「基本の徹底」が生み出す、圧倒的なゲスト満足度
            </p>

            {/* Property photo with button */}
            <div className="mt-6 rounded-xl overflow-hidden shadow-sm border border-stone-200 flex-1 relative">
              <img
                src="https://customer-assets.emergentagent.com/job_rental-experts-1/artifacts/q3pmogmz_ACOE_45x45.jpg"
                alt="ACOE ブランドイメージ"
                className="w-full h-[280px] object-cover"
              />
              <a
                href="https://www.acoe.jp/"
                target="_blank"
                rel="noopener noreferrer"
                className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-stone-800 hover:bg-stone-700 text-stone-50 px-5 py-3 rounded-lg shadow-lg transition-all duration-300 hover:shadow-xl"
              >
                <span className="text-base">公式サイトを見る</span>
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
          </ScrollReveal>

          {/* Right: ACOE Asakusa Case Study */}
          <ScrollReveal direction="right" delay={100}>
            <div className="bg-gradient-to-br from-stone-800 to-stone-900 rounded-2xl p-8 lg:p-10 text-stone-50">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-12 h-12 rounded-xl bg-stone-700 flex items-center justify-center">
                  <BarChart3 className="w-6 h-6 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-xl font-medium text-stone-50">収益実績事例</h3>
                </div>
              </div>

              {/* Revenue comparison */}
              <div className="space-y-6 mb-8">
              {/* Long-term rental */}
              <div className="bg-stone-700/50 rounded-xl p-5 border border-stone-600/30">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-stone-400 text-base">長期賃貸（月額）</span>
                  <span className="text-sm text-stone-500 bg-stone-700 px-2 py-0.5 rounded">比較基準</span>
                </div>
                <p className="text-3xl font-light text-stone-200">
                  ¥1,300,000
                </p>
              </div>

              {/* Arrow */}
              <div className="flex justify-center">
                <div className="w-10 h-10 rounded-full bg-amber-600/20 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-amber-400" />
                </div>
              </div>

              {/* Short-term rental (ACOE) */}
              <div className="bg-gradient-to-br from-amber-600/20 to-amber-700/10 rounded-xl p-5 border border-amber-500/30">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-amber-300 text-base">ACOE運営（月平均）</span>
                  <span className="text-sm text-amber-400 bg-amber-600/20 px-2 py-0.5 rounded">2025年実績</span>
                </div>
                <p className="text-4xl font-light text-stone-50">
                  ¥5,174,000
                </p>
              </div>
            </div>

            {/* Multiplier highlight */}
            <div className="text-center py-6 border-t border-stone-700">
              <p className="text-stone-400 text-base mb-2">長期賃貸比</p>
              <div className="inline-flex items-baseline gap-1">
                <AnimatedNumber 
                  value={3.98} 
                  decimals={2}
                  className="text-6xl font-light text-amber-400"
                />
                <span className="text-3xl text-amber-400/70">倍</span>
              </div>
              <p className="text-stone-500 text-sm mt-3 mb-6">
                ※ 物件により結果は異なります
              </p>
              
              {/* Data disclaimer box */}
              <div className="mt-8 mx-4 p-4 bg-stone-700/30 border border-stone-600/40 rounded-lg">
                <p className="text-amber-300/90 text-sm leading-relaxed">
                  実績データは運営物件の実数値に基づいています。<br />
                  詳細は適合性チェック後にご説明いたします。
                </p>
              </div>
            </div>
            </div>
          </ScrollReveal>

        </div>

        {/* Guest Testimonials Section */}
        <div className="mt-20">
          <div className="text-center mb-12">
            <p className="text-amber-800/80 text-sm tracking-[0.2em] uppercase mb-3">
              Guest Reviews
            </p>
            <h3 className="text-2xl md:text-3xl font-light text-stone-800 mb-4">
              ゲストからの声
            </h3>
            <div className="w-16 h-px bg-amber-700/40 mx-auto" />
          </div>

          <TestimonialSlider />
        </div>
      </div>
    </section>
  );
};

// Testimonial data
const testimonials = [
  {
    name: "Lucio",
    initial: "L",
    bgColor: "bg-amber-100",
    textColor: "text-amber-700",
    platform: "Airbnb",
    platformColor: "bg-[#FF5A5F] hover:bg-[#e04e52]",
    review: "I stayed once again in a property managed by ACOE, and as always, it was an excellent experience. The company is very professional and well organized. Asawa was always responsive, answered all my questions promptly, and provided great support throughout the stay. If a property is managed by ACOE, you can book with confidence — they are truly reliable and excellent professionals.",
    link: "https://www.airbnb.com/rooms/1528530116573578631?guests=1&adults=1&s=67&unique_share_id=acoe-realty"
  },
  {
    name: "Mane Neha",
    initial: "M",
    bgColor: "bg-blue-100",
    textColor: "text-blue-700",
    platform: "Booking.com",
    platformColor: "bg-blue-600 hover:bg-blue-700",
    review: "Thank you so much!! We had a lovely time here, so much fun and made so many memories (especially at your dining table :p) I will write you tomorrow after we leave",
    link: "https://www.booking.com/Share-mXvLQcr"
  },
  {
    name: "Tommy",
    initial: "T",
    bgColor: "bg-green-100",
    textColor: "text-green-700",
    platform: "Airbnb",
    platformColor: "bg-[#FF5A5F] hover:bg-[#e04e52]",
    review: "We had a great stay! The home was very clean, comfortable, and well maintained, and the hosts were great and very welcoming.",
    link: "https://www.airbnb.com/rooms/909834219389134686?guests=1&adults=1&s=67&unique_share_id=acoe-realty"
  },
  {
    name: "Bhakta",
    initial: "B",
    bgColor: "bg-purple-100",
    textColor: "text-purple-700",
    platform: "Airbnb",
    platformColor: "bg-[#FF5A5F] hover:bg-[#e04e52]",
    review: "This was a wonderful home for our group of 7. It was very clean and had a well-equipped kitchen for quick meals. The location is perfect (very close to the train station), and the host was incredibly courteous and helpful. Highly recommended!",
    link: "https://www.airbnb.com/rooms/1355642765734447529?guests=1&adults=1&s=67&unique_share_id=acoe-realty"
  },
  {
    name: "Zakki",
    initial: "Z",
    bgColor: "bg-orange-100",
    textColor: "text-orange-700",
    platform: "Airbnb",
    platformColor: "bg-[#FF5A5F] hover:bg-[#e04e52]",
    review: "Asawa was not only one of the most responsive host we have had, but the quickest, friendliest, pro-active; and so informative, it was like having a tour guide beside us the whole time. :) The apartment was very clean, train and bus stations nearby, grocery shops and restaurants too. If you'd like to stay away from hectic tokyo, but just a train ride away, would recommend Asawa's place.",
    link: "https://www.airbnb.com/rooms/1244569771286796108?guests=1&adults=1&s=67&unique_share_id=acoe-realty"
  }
];

// Testimonial Card Component
const TestimonialCard = ({ testimonial }) => (
  <div className="bg-gradient-to-br from-stone-50 to-amber-50/30 rounded-2xl p-6 lg:p-8 border border-stone-100 h-full flex flex-col">
    <div className="flex items-center gap-3 mb-4">
      <div className={`w-12 h-12 rounded-full ${testimonial.bgColor} flex items-center justify-center`}>
        <span className={`text-xl font-medium ${testimonial.textColor}`}>{testimonial.initial}</span>
      </div>
      <div>
        <h4 className="font-medium text-stone-800">{testimonial.name}</h4>
        <div className="flex items-center gap-1">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
          ))}
        </div>
      </div>
      <a
        href={testimonial.link}
        target="_blank"
        rel="noopener noreferrer"
        className={`ml-auto px-2 py-1 ${testimonial.platformColor} rounded text-white text-xs font-medium transition-colors`}
      >
        {testimonial.platform}
      </a>
    </div>
    <blockquote className="text-stone-600 text-sm leading-relaxed italic flex-grow">
      "{testimonial.review}"
    </blockquote>
    <a
      href={testimonial.link}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 mt-4 text-xs text-amber-700 hover:text-amber-800 transition-colors"
    >
      <span>レビューを見る</span>
      <ExternalLink className="w-3 h-3" />
    </a>
  </div>
);

// Testimonial Slider Component
const TestimonialSlider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Show 2 testimonials at a time on desktop, 1 on mobile
  const itemsPerPage = typeof window !== 'undefined' && window.innerWidth >= 768 ? 2 : 1;
  const totalPages = Math.ceil(testimonials.length / itemsPerPage);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? totalPages - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev === totalPages - 1 ? 0 : prev + 1));
  };

  const getVisibleTestimonials = () => {
    const start = currentIndex * itemsPerPage;
    return testimonials.slice(start, start + itemsPerPage);
  };

  return (
    <div className="relative">
      {/* Navigation Arrows */}
      <button
        onClick={goToPrevious}
        className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-12 z-10 p-2 rounded-full bg-white shadow-md border border-stone-200 hover:bg-stone-50 transition-colors"
        aria-label="Previous testimonials"
      >
        <ChevronLeft className="w-5 h-5 text-stone-600" />
      </button>
      
      <button
        onClick={goToNext}
        className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-12 z-10 p-2 rounded-full bg-white shadow-md border border-stone-200 hover:bg-stone-50 transition-colors"
        aria-label="Next testimonials"
      >
        <ChevronRight className="w-5 h-5 text-stone-600" />
      </button>

      {/* Testimonials Container */}
      <div className="overflow-hidden px-2">
        <div 
          className="grid md:grid-cols-2 gap-6 transition-all duration-500 ease-in-out"
        >
          {getVisibleTestimonials().map((testimonial, index) => (
            <div key={`${currentIndex}-${index}`} className="animate-in fade-in slide-in-from-right-5 duration-500">
              <TestimonialCard testimonial={testimonial} />
            </div>
          ))}
        </div>
      </div>

      {/* Dot Indicators */}
      <div className="flex justify-center gap-2 mt-8">
        {[...Array(totalPages)].map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentIndex
                ? 'bg-amber-600 w-6'
                : 'bg-stone-300 hover:bg-stone-400'
            }`}
            aria-label={`Go to page ${index + 1}`}
          />
        ))}
      </div>

      {/* Page Counter */}
      <p className="text-center text-sm text-stone-500 mt-3">
        {currentIndex + 1} / {totalPages}
      </p>
    </div>
  );
};

export default Results;